import { GetServerSideProps } from 'next';
import Head from 'next/head';
import Link from 'next/link';
import CategoryBanner, { CategoryBannerProps } from 'components/CategoryBanner/CategoryBanner';
import { SeoMetaTags, SeoBlock } from 'constants/dataTypes';
import { getPageHeaderData, getSeoMetaTags, getSeoBlockContent, getPageDataFromLaravel } from 'utils/common.utils';
import { generateImage } from 'utils/image.utils';
import CategorySeoText from 'components/Others/CategorySeoText';
import GlobalSeoMetaTags from 'components/GlobalSeoMetaTags/GlobalSeoMetaTags';
import Image from 'next/image';
import CircleArrow from 'components/Icons/CircleArrow';

interface ProductCategory {
  title: string;
  slug: string;
  listing_image: string;
  listing_image_alt: string;
}

export default function EyewearSelection({
  seoMetaTags,
  categoryBannerProps,
  seoBlock,
  categories
}: {
  seoMetaTags: SeoMetaTags;
  categoryBannerProps: CategoryBannerProps;
  seoBlock: SeoBlock;
  categories: ProductCategory[];
}) {
  const breadcrumbItems = [
    {
      id: 1,
      href: '',
      text: 'Dresden Vision'
    },
    {
      id: 2,
      href: '',
      text: 'Eyewear Selection'
    }
  ];

  return (
    <div>
      <Head>
        <title>Product Selection | Dresden Vision</title>
        <meta
          name="description"
          content="Looking for the perfect fit in affordable prescription glasses in Australia? Well, hello there! Welcome to your one-stop shop for high-quality frames and lenses delivered straight to your door."
        />
      </Head>

      <CategoryBanner {...categoryBannerProps} breadcrumbItems={breadcrumbItems} />
      
      <div className="mx-auto max-w-[1224px] px-4 pt-2">
        <CategorySeoText seoBlock={seoBlock} disableToggle={true} />
        
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mt-8 mb-12">
          {categories.map((category) => (
            <Link
              key={category.slug}
              href={`/eyewear-selection/${category.slug}`}
              className="group relative w-full h-[363px] block overflow-hidden  bg-gray-100 hover:opacity-90 transition-all duration-300"
            >
              <div className="relative w-full h-full">
                <Image
                  src={category.listing_image}
                  alt={category.listing_image_alt}
                  fill
                  className="object-cover transition-transform duration-300 group-hover:scale-105"
                  sizes="(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 392px"
                />
                <div className="absolute inset-0 flex flex-col justify-end">
                  {/* Gradient overlay */}
                  <div className="absolute inset-0 bg-gradient-to-b from-transparent"/>
                  
                  {/* Content container */}
                  <div className="relative flex justify-between items-end p-[30px]"  style={{ background: 'linear-gradient(180deg, rgba(0, 0, 0, 0.00) 0%, rgba(0, 0, 0, 0.75) 89%)' }} >
                    <h2 className="text-white text-[28px] font-semibold leading-[38px] tracking-[-1.08px]">
                      {category.title}
                    </h2>
                    <CircleArrow 
                      className="transition-all duration-300" 
                      strokeColor="#FFFFFF"
                    />
                  </div>
                </div>
              </div>
            </Link>
          ))}
        </div>
      </div>

      <GlobalSeoMetaTags seoMetaTags={seoMetaTags} />
    </div>
  );
}

export const getServerSideProps: GetServerSideProps = async ({ locale }) => {
  const pageSlug = 'eyewear-selection';
  
  const seoMetaTags = await getSeoMetaTags(pageSlug, locale);

  const categoryBannerProps: CategoryBannerProps = {
    title: 'Product Selection',
    description: '<h1>Product Selection</h1><p>Looking for the perfect fit in affordable prescription glasses in Australia? Well, hello there! Welcome to your one-stop shop for high-quality frames and lenses delivered straight to your door. Look sharp and improve your vision with just a couple of clicks.</p>',
    imageSrc: generateImage(pageSlug, 'categories', 'slug', 'banner', 'jpg')
  };

  const pageData = await getPageDataFromLaravel(pageSlug, locale, undefined);
  if (!pageData) {
    return {
      notFound: true
    };
  }

  const notionPageHeaders = await getPageHeaderData(pageSlug, locale, categoryBannerProps);
  const seoBlock = await getSeoBlockContent(pageSlug, locale);

  const categories = pageData.map((item: {
    title: string;
    slug: string;
    listing_image: string;
    listing_image_alt: string;
  }) => ({
    title: item.title,
    slug: item.slug.split('/').pop(),
    listing_image: item.listing_image,
    listing_image_alt: item.listing_image_alt
  }));

  return {
    props: {
      seoMetaTags,
      categoryBannerProps: notionPageHeaders || categoryBannerProps,
      seoBlock,
      categories
    }
  };
};
